import { useState } from 'react';

import { useAlertsStore } from '@/configs/store';
import { parseTimeExpression } from '@/utils/parseTimeInput';

import { DataValues, FormValues } from '../types';
import { normalizeToNoon, normalizeToTime } from '../utils';

type UseTimeLogSubmitProps = {
  onSubmit: (data: DataValues) => Promise<void>;
  onSuccess: () => void;
  userId?: string;
};

type UseTimeLogSubmitReturn = {
  submit: (data: FormValues) => Promise<void>;
  isSubmitting: boolean;
};

export const useTimeLogSubmit = ({
  onSubmit,
  onSuccess,
  userId,
}: UseTimeLogSubmitProps): UseTimeLogSubmitReturn => {
  const { addAlert } = useAlertsStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (data: FormValues) => {
    const usersIds = data.users?.map((u) => u.uid) ?? [];

    if (!data.taskKey) return;

    setIsSubmitting(true);

    try {
      const entries = data.dateEntries;

      for (const entry of entries) {
        const startDateTime = entry.startTime
          ? normalizeToTime(entry.startDate, entry.startTime)
          : normalizeToNoon(entry.startDate);

        const duration = parseTimeExpression(entry.duration);

        if (!duration) continue;

        await onSubmit({
          taskKey: data.taskKey,
          duration,
          comment: data.comment,
          startDate: startDateTime,
          usersIds,
        });
      }

      onSuccess();
    } catch (error) {
      console.error('Ошибка при сохранении лога:', error);

      addAlert({
        message: 'Не удалось сохранить лог',
        title: 'Ошибка',
        autoClose: true,
        withReload: false,
      });
    } finally {
      setIsSubmitting(false);

      if (!usersIds.includes(userId ?? '')) {
        addAlert({
          message: 'Лог успешно сохранен',
          title: 'Успех',
          autoClose: true,
          withReload: false,
        });
      }
    }
  };

  return { submit, isSubmitting };
};
